import React, {ChangeEvent, useEffect, useMemo, useRef, useState} from "react";
import {HandleSaveResponse, StrategyHeader} from "./components/StrategyHeader";
import {StrategySidebar} from "./components/StrategySidebar";
import {DataEntrySummaryItem} from "../components/DataEntry/DataEntrySummary";
import {
    CurrencyInput,
    EmptyStateContainer,
    InfoPopover,
    Input,
    RedAsterisk,
    RequiredFieldsSubheader,
    UnderlinedHeader,
} from "../components";
import {
    formatCurrency,
    formatNumber,
    formatNumberRoundedToTwoDecimals,
    formatPercentWithTwoDecimals,
    formatPercentWithWholeNumberAndTwoDecimals
} from "../utils/format";
import PercentInput from "../components/Input/PercentInput";
import {YearsInput} from "../components/YearsInput/YearsInput";
import {wealthPOApiClient} from "./WealthPOApiClient";
import {useParams} from "react-router-dom";
import {RouteWithIdAndStrategyId} from "../routes/types";
import {
    EstimatedImpact,
    FailedAPICall,
    GRATRates,
    GRATStrategy,
    HurdleRateDTO,
    initialEstimatedImpact,
    initialFiveYearGRATStrategy,
    initialPayoutPercentagesFive,
    initialPayoutPercentagesTwo,
    initialRollingGRATStrategy,
    initialTwoYearGRATStrategy,
    PayoutPercentagesRequestDTO,
    StrategyPayoutScheduleItem,
    StrategyType,
} from "./WealthPOTypes";
import useProfileEditableState from "../hooks/useProfileEditableState";
import GenericErrorModal, {
    genericEmptyErrorModalData,
    GenericErrorModalData
} from "../components/Modal/Error/GenericErrorModal";
import {selectGRATStrategies} from "./WealthPlanOptimizerSlice";
import {useAppSelector} from "../store/hooks";
import ScrollableContainer from "../components/ScrollableContainer/ScrollableContainer";
import {
    DateAndAgeValues,
    getDateAndAgeValues,
    PlanningPeriodValues,
    processInvestorGroupResponse,
    validateDescription,
    validateYearsOfFlow,
    validateYearsOfFlowRollingGrat
} from "./WealthPOUtils";
import {InvestorGroupType} from "../ClientManagement/models/InvestorGroupType";
import {clientManagementApiClient} from "../ClientManagement/ClientManagementApiClient";
import {Table} from "xps-react";
import {HttpOptions} from "../core/api";
import {SubMenuProps} from "../models/routeData/RouteParamTypes";

export interface AddEditGRATProps {
    strategyType: StrategyType,
    header: string,
    refreshStrategiesSummary: () => void,
    items: SubMenuProps[]
}

export enum GRATErrorConditions {
    INCREASE_IN_PAYMENT_LIMIT,
    YOF_OUT_OF_BOUNDS,
    YOF_PLANNING_PERIOD_YEARS,
}

const AddEditGRAT: React.FC<AddEditGRATProps> = ({
                                                     strategyType,
                                                     header,
                                                     refreshStrategiesSummary,
                                                     items
                                                 }) => {
    const MAX_ANNUITY_RATE: number = 100;

    const {id, strategyId} = useParams<RouteWithIdAndStrategyId>();
    const strategies = useAppSelector(selectGRATStrategies);
    let strategy: GRATStrategy = {
        strategyId: strategyId,
        strategyType: strategyType,
        description: "",
        contributionAmount: 0,
        increaseInPayment: 20,
        annuityRate: undefined,
        yearsOfFlow: 0,
        estimatedImpact: initialEstimatedImpact,
        enabled: true
    };

    let defaultPayoutPercentages: number[] = [];
    switch (strategyType) {
        case StrategyType.TWO_YEAR_GRAT:
            strategy = initialTwoYearGRATStrategy;
            defaultPayoutPercentages = initialPayoutPercentagesTwo.payoutPercentages;
            break;
        case StrategyType.FIVE_YEAR_GRAT:
            strategy = initialFiveYearGRATStrategy;
            defaultPayoutPercentages = initialPayoutPercentagesFive.payoutPercentages;
            break;
        case StrategyType.ROLLING_GRAT:
            strategy = initialRollingGRATStrategy;
            defaultPayoutPercentages = initialPayoutPercentagesTwo.payoutPercentages;
            break;
        default:
            break;
    }

    if (strategyId) {
        const foundStrategy = strategies.find(s => s.strategyId === strategyId);
        if (foundStrategy) {
            strategy = foundStrategy;
        }
    }

    const [description, setDescription] = useState<string>(strategy.description);
    const [contributionAmountDisplayValue, setContributionAmountDisplayValue] = useState<number>(strategy.contributionAmount);
    const [contributionAmount, setContributionAmount] = useState<number>(strategy.contributionAmount);
    const [increaseInPaymentDisplayValue, setIncreaseInPaymentDisplayValue] = useState<string>(strategy.increaseInPayment.toString());
    const [increaseInPayment, setIncreaseInPayment] = useState<number>(strategy.increaseInPayment);
    const [annuityRateDisplayValue, setAnnuityRateDisplayValue] = useState<string>(strategy.annuityRate !== undefined ? strategy.annuityRate.toString() : "0.00");
    const [annuityRate, setAnnuityRate] = useState<number | undefined>(strategy.annuityRate);
    const [referenceDataHurdleRateDto, setReferenceDataHurdleRateDto] = useState<HurdleRateDTO>({
        hurdleRate: 0,
        hurdleRateMonth: "",
        hurdleRateYear: ""
    })
    const [payoutPercentages, setPayoutPercentages] = useState<number[]>(defaultPayoutPercentages)

    const [yearsOfFlowDisplayValue, setYearsOfFlowDisplayValue] = useState<number>(strategy.yearsOfFlow);
    const [yearsOfFlow, setYearsOfFlow] = useState<number>(strategy.yearsOfFlow);
    const [endDate, setEndDate] = useState<string>("");
    const [startYearDisplayValue, setStartYearDisplayValue] = useState<number | string>("");
    const [endYearDisplayValue, setEndYearDisplayValue] = useState<number | string>("");

    const [error, setError] = React.useState<GenericErrorModalData>(genericEmptyErrorModalData);
    const [showTempErrorMessage, setShowTempErrorMessage] = useState(false);
    const [estimatedImpact, setEstimatedImpact] = useState<EstimatedImpact>(strategy.estimatedImpact)
    const [validationError, setValidationError] = useState<boolean>(false);
    const [inputError, setInputError] = useState<GRATErrorConditions | undefined>(undefined)

    const contributionAmountChanged = useRef<boolean>(false);
    const increaseInPaymentChanged = useRef<boolean>(false);
    const annuityRateChanged = useRef<boolean>(false);
    const yearsOfFlowChanged = useRef<boolean>(false);

    const {isProfileWithProposalsOrArchived} = useProfileEditableState();
    const refDataAbortControllerRef = useRef<AbortController | null>(null);
    const estimatedImpactAbortControllerRef = useRef<AbortController | null>(null);
    const payoutPercentagesAbortControllerRef = useRef<AbortController | null>(null);
    const failedApiCall = useRef<FailedAPICall>();

    const [planningPeriodValues, setPlanningPeriodValues] = useState<PlanningPeriodValues>({
        birthdate: "",
        firstName: "",
        planningPeriod: undefined
    })

    const handleContributionAmountOnBlur = () => {
        let newValue = Number(contributionAmountDisplayValue);
        setContributionAmount(newValue);

        if (newValue == 0) {
            setPayoutPercentages(defaultPayoutPercentages);
        } else if (isPayoutPercentagesEmpty()) {
            fetchPayoutPercentages(payoutPercentagesAbortControllerRef.current?.signal);
        }

        if (!contributionAmountChanged.current) {
            contributionAmountChanged.current = true;
        }
    }

    const handleIncreaseInPaymentOnBlur = () => {
        let newValue = Number(increaseInPaymentDisplayValue);

        if (newValue > 20) {
            setInputError(GRATErrorConditions.INCREASE_IN_PAYMENT_LIMIT)
            setIncreaseInPaymentDisplayValue(increaseInPayment.toString())
            return;
        }

        setIncreaseInPayment(newValue);
        if (!increaseInPaymentChanged.current) {
            increaseInPaymentChanged.current = true;
        }
    }

    const handleAnnuityRateOnBlur = () => {
        let newValue: number = Number(annuityRateDisplayValue) || 0;

        let isValid = validateAnnuityRate(newValue);

        if (isValid) {
            setAnnuityRate(newValue);
            setAnnuityRateDisplayValue(formatNumberRoundedToTwoDecimals(newValue));
        } else {
            setAnnuityRate(MAX_ANNUITY_RATE);
        }

        if (!annuityRateChanged.current) {
            annuityRateChanged.current = true;
        }
    }

    const validateAnnuityRate = (newValue: number) => {
        return newValue <= MAX_ANNUITY_RATE;
    }

    const handleYearsOfFlowOnBlur = () => {
        let newValue: number = Number(yearsOfFlowDisplayValue);

        setInputError(undefined);

        let validationResponse;
        if (strategyType === StrategyType.ROLLING_GRAT) {
            validationResponse = validateYearsOfFlowRollingGrat(newValue, yearsOfFlow, planningPeriodValues.planningPeriod?.numberOfYears);
        } else {
            validationResponse = validateYearsOfFlow(newValue, 0, yearsOfFlow, planningPeriodValues);
        }

        if (validationResponse.errorPresent) {
            setInputError(validationResponse.errorType);
            setYearsOfFlowDisplayValue(yearsOfFlow);
        }

        if (validationResponse.newYearsOfFlow !== yearsOfFlow) {
            setYearsOfFlowDisplayValue(validationResponse.newYearsOfFlow)
            setYearsOfFlow(validationResponse.newYearsOfFlow)
        }

        if (!yearsOfFlowChanged.current) {
            yearsOfFlowChanged.current = true;
        }
    }

    const handleSave = async (): Promise<HandleSaveResponse> => {
        setValidationError(false)

        const valid = validateDescription(description);

        let response = {
            isSuccess: valid,
            errors: {
                hasValidationError: false,
                hasCommunicationError: false,
                traceId: ""
            }
        } as HandleSaveResponse

        if (!valid) {
            setValidationError(true)

            response.isSuccess = false
            response.errors.hasValidationError = true
            return response;
        } else {
            let requestBody = {
                strategyType: strategyType,
                contributionAmount,
                description,
                increaseInPayment,
                annuityRate,
                yearsOfFlow
            } as GRATStrategy

            try {
                if (strategyId) {
                    requestBody = {
                        ...requestBody,
                        strategyId
                    }

                    await wealthPOApiClient.editGRATStrategy(id, requestBody);
                } else {
                    await wealthPOApiClient.createGRATStrategy(id, requestBody);
                }
            } catch (saveError: Error | any) {
                response.isSuccess = false;
                response.errors.hasCommunicationError = true;
                response.errors.traceId = saveError.headers.get('trace-id');
            }

            return response;
        }
    }

    const handleCancel = () => {
        let formattedAnnuityRate = formatNumber(annuityRate ?? 0.00);
        let formattedHurdleRate = formatPercentWithWholeNumberAndTwoDecimals(referenceDataHurdleRateDto.hurdleRate, false);
        let formattedExistingStrategyAnnuityRate = formatNumber(strategy.annuityRate ?? 0.00);

        return description !== strategy.description
            || contributionAmount !== strategy.contributionAmount
            || increaseInPayment !== strategy.increaseInPayment
            || yearsOfFlow !== strategy.yearsOfFlow
            || (strategy.annuityRate === undefined && formattedAnnuityRate !== formattedHurdleRate)
            || (strategy.annuityRate !== undefined && formattedAnnuityRate !== formattedExistingStrategyAnnuityRate);
    }

    const setDisplayAndRecomputedValues = (dateAndAgeComputedValues: DateAndAgeValues) => {
        setEndDate(dateAndAgeComputedValues.endDate)
        setStartYearDisplayValue(dateAndAgeComputedValues.startYear)
        setEndYearDisplayValue(dateAndAgeComputedValues.endYear)
    }

    const fetchReferenceData = async (signal: AbortSignal | undefined) => {
        const apiRequests: [Promise<GRATRates>, Promise<InvestorGroupType>] = [
            wealthPOApiClient.getGRATRatesReferenceData(id, {signal} as HttpOptions),
            clientManagementApiClient.getInvestorGroup(id, {signal} as HttpOptions)
        ]

        try {
            let [gratRates, investorGroup] = await Promise.all(apiRequests);
            const hurdleRateDto = gratRates.hurdleRateDTO;

            if (!strategyId) {
                const formattedReferenceDataHurdleRate = formatPercentWithWholeNumberAndTwoDecimals(hurdleRateDto.hurdleRate, false);
                setAnnuityRate(Number(formattedReferenceDataHurdleRate));
                setAnnuityRateDisplayValue(formattedReferenceDataHurdleRate);
            } else {
                let existingStrategyAnnuityRate = formatNumber(strategy.annuityRate ?? 0.00);
                setAnnuityRate(Number(existingStrategyAnnuityRate));
                setAnnuityRateDisplayValue(existingStrategyAnnuityRate);
            }

            setReferenceDataHurdleRateDto(hurdleRateDto)

            const {
                planningPeriodComputedValues,
                dateAndAgeComputedValues
            } = processInvestorGroupResponse(investorGroup, 0, yearsOfFlow);

            setPlanningPeriodValues(planningPeriodComputedValues);
            setDisplayAndRecomputedValues(dateAndAgeComputedValues);

        } catch (err) {
            failedApiCall.current = FailedAPICall.REFERENCE_DATA;
            openErrorModal(err)
        } finally {
            refDataAbortControllerRef.current = null;
        }
    }

    useEffect(() => {
        refDataAbortControllerRef.current = new AbortController();
        const {signal} = refDataAbortControllerRef.current;

        void fetchReferenceData(signal);

        return () => {
            if (refDataAbortControllerRef.current) {
                refDataAbortControllerRef.current.abort();
                refDataAbortControllerRef.current = null;
            }
        }
    }, [id])

    const isPayoutPercentagesEmpty = () => {
        return payoutPercentages[0] == 0;
    }

    const fetchPayoutPercentages = async (signal: AbortSignal | undefined) => {
        if (annuityRate) {
            if (contributionAmountDisplayValue > 0) {
                const payoutPercentagesRequestDTO = {
                    contributionAmount: Number(contributionAmountDisplayValue),
                    increaseInPayment,
                    annuityRate,
                    strategyType
                } as PayoutPercentagesRequestDTO

                await wealthPOApiClient.getPayoutPercentages(id, payoutPercentagesRequestDTO, {signal} as HttpOptions)
                    .then(payoutPercentagesResponseDTO => {
                        setPayoutPercentages(payoutPercentagesResponseDTO.payoutPercentages)
                    })
                    .catch(err => {
                        if (err.name !== 'AbortError') {
                            failedApiCall.current = FailedAPICall.PAYOUT_PERCENTAGES;
                            openErrorModal(err)
                        }
                    })
                    .finally(() => {
                        payoutPercentagesAbortControllerRef.current = null;
                    })
            } else {
                setPayoutPercentages(defaultPayoutPercentages);
            }
        }
    }

    useEffect(() => {
        payoutPercentagesAbortControllerRef.current = new AbortController();
        const {signal} = payoutPercentagesAbortControllerRef.current;

        void fetchPayoutPercentages(signal);

        return () => {
            if (payoutPercentagesAbortControllerRef.current) {
                payoutPercentagesAbortControllerRef.current.abort();
                payoutPercentagesAbortControllerRef.current = null;
            }
        }
    }, [increaseInPayment, annuityRate])

    useEffect(() => {
        let dateAndAgeComputedValues: DateAndAgeValues = getDateAndAgeValues(planningPeriodValues.birthdate, 0, yearsOfFlow);
        setDisplayAndRecomputedValues(dateAndAgeComputedValues);
    }, [yearsOfFlow])

    const fetchEstimatedImpact = async (signal: AbortSignal | undefined) => {
        if (strategy.strategyType) {
            if (isPayoutPercentagesEmpty() || contributionAmount <= 0) {
                const zeroPayoutSchedule = createPayoutScheduleWithZeros();
                setEstimatedImpact({
                    amountToBeneficiaries: undefined,
                    impactToEstEstateTax: undefined,
                    strategyPayoutSchedule: zeroPayoutSchedule
                } as EstimatedImpact)
            } else {
                wealthPOApiClient.getEstimatedImpactForGRAT(id, {
                    strategyId: undefined,
                    strategyType: strategyType,
                    contributionAmount: contributionAmount,
                    payoutPercentages: payoutPercentages,
                    yearsOfFlow: yearsOfFlow,
                    enabled: true
                }, {signal} as HttpOptions)
                    .then(estimatedImpactResponse => {
                        setEstimatedImpact(estimatedImpactResponse);
                    })
                    .catch((err) => {
                        if (err.name !== 'AbortError') {
                            failedApiCall.current = FailedAPICall.ESTIMATED_IMPACT;
                            openErrorModal(err);
                        }
                    })
                    .finally(() => {
                        estimatedImpactAbortControllerRef.current = null;
                    })
            }
        }
    }

    const createPayoutScheduleWithZeros = () => {
        let payoutSchedules = [];
        for (let i = 0; i < yearsOfFlow; i++) {
            payoutSchedules.push({
                index: Number(startYearDisplayValue) + i,
                contributionAmount: 0,
                year1Annuity: 0,
                year2Annuity: 0,
                remainder: 0
            } as StrategyPayoutScheduleItem)
        }
        return payoutSchedules;
    }

    useEffect(() => {
        estimatedImpactAbortControllerRef.current = new AbortController();
        const {signal} = estimatedImpactAbortControllerRef.current;

        void fetchEstimatedImpact(signal);

        return () => {
            if (estimatedImpactAbortControllerRef.current) {
                estimatedImpactAbortControllerRef.current.abort();
                estimatedImpactAbortControllerRef.current = null;
            }
        }
    }, [contributionAmount, payoutPercentages, yearsOfFlow])

    useEffect(() => {
        const timeout = setTimeout(() => {
            setInputError(undefined);
        }, 6000);
        return () => {
            clearTimeout(timeout);
        }
    }, [inputError]);

    const openErrorModal = (e: Error | any) => {
        setError({
            isOpen: true,
            header: "Unable to Load This Page",
            message: (
                <>
                    <p>Check your VPN connection and retry.</p>
                    <p>If the problem persists, contact <a href="mailto:GPIITSupport@ntrs.com">GPS Technical
                        Support</a> at GPIITSupport@ntrs.com and provide the following details:</p>
                </>
            ),
            profileId: id,
            time: new Date(),
            errorDetail: `Failed to load ${header} page (${e.status})`,
            operationId: e.headers?.get('trace-id')
        })
    }

    const closeErrorModal = () => {
        setError({...error, isOpen: false});
    };

    const handleRetryClickInCommunicationErrorModal = () => {
        closeErrorModal();
        switch (failedApiCall.current) {
            case(FailedAPICall.REFERENCE_DATA):
                void fetchReferenceData(refDataAbortControllerRef.current?.signal);
                break;
            case(FailedAPICall.PAYOUT_PERCENTAGES):
                void fetchPayoutPercentages(payoutPercentagesAbortControllerRef.current?.signal);
                break;
            case(FailedAPICall.ESTIMATED_IMPACT):
                void fetchEstimatedImpact(estimatedImpactAbortControllerRef.current?.signal);
                break;
        }
    }

    const handleCloseClickInCommunicationErrorModal = () => {
        closeErrorModal();
        setShowTempErrorMessage(true);
    }

    const memoizedErrorMessage = useMemo(() => {
        let errorMessage;

        switch (inputError) {
            case GRATErrorConditions.INCREASE_IN_PAYMENT_LIMIT:
                errorMessage = "Increase in payment cannot be more than 20%."
                break;
            case GRATErrorConditions.YOF_OUT_OF_BOUNDS:
                errorMessage = "Enter a Value from 3 to 10";
                break;
            case GRATErrorConditions.YOF_PLANNING_PERIOD_YEARS:
                errorMessage = "Strategy Time Frame cannot exceed the Planning Period";
                break;
            default:
                errorMessage = ""
        }

        return inputError !== undefined && (
            <>
                <div></div>
                <div data-testid={`error-message-${inputError}`}
                     className="strategy-time-frame-error-msg">
                    {errorMessage}
                </div>
            </>
        )
    }, [inputError])

    const memoizedStrategySummary = useMemo(() => {
        return [
            {
                label: "Contribution Amount",
                value: formatCurrency(contributionAmount),
                testId: "contributionAmount"
            } as DataEntrySummaryItem,
            {
                label: "% Increase in Payment",
                value: `${formatNumberRoundedToTwoDecimals(increaseInPayment)}%`,
                testId: "increaseInPayment"
            } as DataEntrySummaryItem,
            {
                label: "Annuity Rate",
                value: `${formatNumberRoundedToTwoDecimals(annuityRate ?? 0)}%`,
                testId: "annuityRate"
            } as DataEntrySummaryItem
        ]
    }, [contributionAmount, increaseInPayment, annuityRate]);

    const memoizedEstimatedImpact: DataEntrySummaryItem[] = useMemo(() => {
        return [
            {
                label: "Future Value of Gift",
                value: (estimatedImpact.amountToBeneficiaries !== undefined) ? formatCurrency(estimatedImpact.amountToBeneficiaries) : undefined,
                testId: "amountToBeneficiaries",
            } as DataEntrySummaryItem,
            {
                label: "Est. Estate Tax",
                value: (estimatedImpact.impactToEstEstateTax !== undefined) ? formatCurrency(estimatedImpact.impactToEstEstateTax) : undefined,
                testId: "estEstateTax",
            } as DataEntrySummaryItem,
        ]
    }, [estimatedImpact]);

    const memoizedStrategyPayoutScheduleTableRows = useMemo(() => {
        const startYear = Number(startYearDisplayValue);
        const endYear = Number(endYearDisplayValue);
        const payoutSchedule = estimatedImpact.strategyPayoutSchedule;
        return Array.from({length: endYear - startYear - 1}, (_, i) => ({
            gratYear: `GRAT ${i + 1}`,
            contributionAmount: formatCurrency(payoutSchedule[i]?.contributionAmount || 0),
            year1: formatCurrency(payoutSchedule[i]?.year1Annuity || 0),
            year2: formatCurrency(payoutSchedule[i]?.year2Annuity || 0),
            remainder: formatCurrency(payoutSchedule[i]?.remainder || 0)
        }));
    }, [estimatedImpact.strategyPayoutSchedule, startYearDisplayValue, endYearDisplayValue])

    const renderPlanningPeriodRow = (testId: string) => {
        return (
            <div className="layout-data-entry-form__field"
                 data-testid={testId}>
                <label className={"h5"}>Planning Period</label>
                <span
                    className={`inputError && 'error-color'`}
                    role="planningPeriod"
                    aria-label="planningPeriod">{`${planningPeriodValues.planningPeriod?.numberOfYears} years (${planningPeriodValues.firstName})`}</span>
            </div>
        )
    }

    if (error.isOpen) {
        return (
            <GenericErrorModal
                errorModalData={error}
                onClickButton={handleRetryClickInCommunicationErrorModal}
                onRequestClose={handleCloseClickInCommunicationErrorModal}
                buttonText="Retry"
                buttonProps={
                    {
                        primary: true,
                        className: 'full-width center-align',
                        iconPosition: 'left',
                        iconName: 'refresh'
                    }
                }
                showAlertIcon={false}
            />
        )
    }

    if (showTempErrorMessage) {
        return (
            <EmptyStateContainer
                className="no-wpo-summary-placeholder"
                title="Unable to Load This Page"
                size="large"
                description="Try again later."
            />
        )
    }

    return (
        <ScrollableContainer id={"add-edit-grat-scroll-container"} className="wealth-plan-optimizer">
            <StrategyHeader
                header={header}
                handleSave={handleSave}
                handleCancel={handleCancel}
                refreshStrategySummary={refreshStrategiesSummary}
            />

            <div className="wealthpo-strategy__form">
                <article>
                    <section aria-label="Strategy Details">
                        <UnderlinedHeader
                            className={"wealthpo-strategy__underlined-header"}
                            primaryText="Strategy Details"
                            rightAlignedContent={<RequiredFieldsSubheader/>}
                        />
                        <div className="layout-data-entry-form__field">
                            <label className={"h5"} data-testid={'description'}>Description<RedAsterisk/></label>
                            <Input
                                name="descriptionField"
                                aria-label="description"
                                aria-labelledby="descriptionFieldInput-label"
                                id="descriptionFieldInput"
                                removeMarginTop
                                size="medium"
                                type="text"
                                value={description}
                                readOnly={isProfileWithProposalsOrArchived}
                                error={validationError ? "Description is required." : undefined}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                            />
                        </div>
                        <div className="layout-data-entry-form__field" style={{alignItems: "start"}}>
                            <label className={"h5"} data-testid={'contributionAmountInput'}>Contribution Amount</label>
                            <div className="annual-flow-input-field">
                                <CurrencyInput
                                    name="contributionAmountField"
                                    aria-label="contributionAmount"
                                    id="contributionAmountFieldInput"
                                    size="medium"
                                    value={contributionAmountDisplayValue}
                                    readOnly={isProfileWithProposalsOrArchived}
                                    textAlign='left'
                                    onChangeValue={e => isNaN(parseInt(e.target.value)) ? setContributionAmountDisplayValue(0) : setContributionAmountDisplayValue(parseInt(e.target.value))}
                                    onBlur={handleContributionAmountOnBlur}
                                />
                            </div>
                        </div>
                        <div className="layout-data-entry-form__field" style={{alignItems: "start"}}>
                            <label className={"h5"} data-testid={'increaseInPaymentInput'}>% Increase in Payment</label>
                            <div className="percent-input-field">
                                <PercentInput
                                    hideLabel
                                    aria-label="increaseInPayment"
                                    label="increaseInPayment"
                                    size="medium"
                                    defaultValue={"20"}
                                    value={increaseInPaymentDisplayValue}
                                    disabled={isProfileWithProposalsOrArchived}
                                    textAlign='left'
                                    onChange={e => setIncreaseInPaymentDisplayValue(e)}
                                    onBlur={handleIncreaseInPaymentOnBlur}
                                />
                            </div>
                        </div>
                        {(inputError !== undefined && inputError === GRATErrorConditions.INCREASE_IN_PAYMENT_LIMIT) && memoizedErrorMessage}
                        <div className="layout-data-entry-form__field" style={{alignItems: "start"}}>
                            <label className={"h5"} data-testid={'annuityRateInput'}>Annuity Rate</label>
                            <div className="percent-input-field">
                                <PercentInput
                                    hideLabel
                                    aria-label="annuityRate"
                                    label="annuityRate"
                                    size="medium"
                                    defaultValue={annuityRateDisplayValue}
                                    value={annuityRateDisplayValue}
                                    disabled={isProfileWithProposalsOrArchived}
                                    textAlign='left'
                                    onChange={e => setAnnuityRateDisplayValue(e)}
                                    onBlur={handleAnnuityRateOnBlur}
                                />
                                <span
                                    className="subheader">7520 Hurdle Rate for month of {referenceDataHurdleRateDto.hurdleRateMonth} {referenceDataHurdleRateDto.hurdleRateYear}: {formatPercentWithTwoDecimals(referenceDataHurdleRateDto.hurdleRate)}</span>
                            </div>
                        </div>
                        <div className="layout-data-entry-form__field">
                            <label className={"h5"}>Growth Rate</label>
                            <div className="growth-rate-value">
                                <span aria-label={'growthRate'}>Risk Asset</span>
                            </div>
                        </div>
                        <div className="layout-data-entry-form__field">
                            <label className={"h5 align-self-start"}>Payout Percentages</label>
                            <div className={"payout-percentages"}>
                                {payoutPercentages.map((value, index) => {
                                    const percentage = formatPercentWithTwoDecimals(value)
                                    return (
                                        <span
                                            key={`payout-percentage-${index + 1}`}
                                            role="payout-percentage"
                                            aria-label="payout-percentage">{`Year ${index + 1} Annuity Rate: ${percentage}`}
                                    </span>
                                    )
                                })}
                            </div>
                        </div>
                    </section>
                    {strategyType === 'ROLLING_GRAT' &&
                        <>
                            <section aria-label="Strategy Time Frame"
                                     data-testid='strategyTimeFrameInfoIcon'>
                                <UnderlinedHeader
                                    className={"wealthpo-strategy__underlined-header"}
                                    primaryText="Strategy Time Frame"
                                    secondaryContent={
                                        <InfoPopover
                                            content={<div
                                                className={"strategy-time-frame__content hover-text"}>
                                                Strategy time frame dates are calculated based on the date of birth of the
                                                primary or secondary client contact with the longest planning horizon.</div>}
                                            direction="top"
                                            width="248px"
                                        />
                                    }
                                />
                                <div className="layout-data-entry-form__field">
                                    <label className={"h5"} data-testid={"yearsOfFlow"}>Years of Flow</label>
                                    <div className="display-flex" data-testid="strategyTimeFrameYearsOfFlow">
                                        <YearsInput style={{width: "134px"}}
                                                    name={"yearsOfFlow"}
                                                    aria-label={"yearsOfFlowInput"}
                                                    className={"StrategyWithTimeFrameYearsOfFlow"}
                                                    value={yearsOfFlowDisplayValue}
                                                    size={"medium"}
                                                    readOnly={isProfileWithProposalsOrArchived ?? false}
                                                    disabled={false}
                                                    onChangeValue={(_e, value) => setYearsOfFlowDisplayValue(value)}
                                                    onBlur={handleYearsOfFlowOnBlur}
                                        />
                                        <div className="strategyTimeFrameDate"
                                             data-testid="strategyTimeFrameYearsOfFlowEndDate">
                                            <label>
                                                <b>End Date</b>
                                            </label>
                                            <span role="endDate"
                                                  aria-label="end-date">{endDate}</span>
                                        </div>
                                    </div>
                                </div>
                                {(inputError !== undefined && (inputError === GRATErrorConditions.YOF_OUT_OF_BOUNDS || inputError === GRATErrorConditions.YOF_PLANNING_PERIOD_YEARS)) && memoizedErrorMessage}
                                {renderPlanningPeriodRow("strategyTimeFramePlanningPeriod")}

                            </section>
                            <section aria-label="Strategy Payout Schedule">
                                <div>
                                    <UnderlinedHeader
                                        className={"wealthpo-strategy__underlined-header"}
                                        primaryText="Strategy Payout Schedule"
                                    />
                                    <div className="strategy-payout-schedule"
                                         data-testid={"strategyPayoutScheduleTable"}>
                                        <Table
                                            id="strategyPayoutScheduleTable"
                                            showPagination={false}
                                            sortable={false}
                                            rowHeight="small"
                                            defaultPageSize={10}
                                            data={memoizedStrategyPayoutScheduleTableRows}
                                            columns={[
                                                {
                                                    Header: "GRAT",
                                                    accessor: "gratYear",
                                                },
                                                {
                                                    Header: "Contribution",
                                                    accessor: "contributionAmount",
                                                },
                                                {
                                                    Header: "Year 1",
                                                    accessor: "year1",
                                                },
                                                {
                                                    Header: "Year 2",
                                                    accessor: "year2",
                                                },
                                                {
                                                    Header: "Remainder",
                                                    accessor: "remainder",
                                                }
                                            ]
                                            }
                                        />
                                    </div>
                                </div>
                            </section>
                        </>
                    }
                </article>

                <StrategySidebar
                    strategySummary={memoizedStrategySummary}
                    estimatedImpact={memoizedEstimatedImpact}
                    items={items}
                    strategyName={"GRAT"}
                />
            </div>

        </ScrollableContainer>
    )
}

export default AddEditGRAT;